import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import ExternalMenuSyncForm from './ExternalMenuSyncForm'
import ExternalMenuModifiers from './ExternalMenuModifiers'
import { httpOk, FILE, PUT } from 'helpers/api'
import { formatMoney, notify } from 'helpers/helpers'
import useFileDrop from 'pages/parts/useFileDrop'
import HintIcon from 'pages/parts/HintIcon'

const EMPTY_SYNC_OPTIONS = {
	name: false,
	price: false,
	clear_price: false,
	image: false,
	weight: false,
	round: null,
	markup: null,
}

function Index({
	showRightBar,
	setShowRightBar,
	toggleModalProduct,
	selectedItem,
	setSelectedItem,
	updateProduct,
	externalGroupId,
	onProductSynced,
}) {
	const { t } = useTranslation()
	const [showSyncPanel, setShowSyncPanel] = useState(false)
	const [syncOptions, setSyncOptions] = useState(EMPTY_SYNC_OPTIONS)
	const [activeTab, setActiveTab] = useState(1)

	const units = [
		{ 'id': 'л', 'name': t('litr') },
		{ 'id': 'кг', 'name': t('kg') },
		{ 'id': 'г', 'name': t('gramm') },
		{ 'id': 'мл', 'name': t('mililitr') },
	]

	useEffect(() => {
		setShowSyncPanel(false)
		setSyncOptions(EMPTY_SYNC_OPTIONS)
		setActiveTab(1)
	}, [selectedItem?.id])

	function toggleSyncPanel() {
		setShowSyncPanel(prev => !prev)
	}

	function closeSyncPanel() {
		setShowSyncPanel(false)
		setSyncOptions(EMPTY_SYNC_OPTIONS)
	}

	async function syncProductFromInternal(e) {
		if (e?.preventDefault) {
			e.preventDefault()
		}

		const response = await PUT('/external-menu-product-sync', {
			...syncOptions,
			id: selectedItem.id,
			external_group_id: externalGroupId,
		}, { loader: true })

		if (httpOk(response) && response.data?.id) {
			onProductSynced(response.data)
			closeSyncPanel()
		}
	}

	async function uploadImageFile(file) {
		if (file) {
			if (!file.type?.startsWith('image/')) {
				notify(t('image2'));
				return;
			}

			const imgSize = file.size / (1024 * 1024);

			if (imgSize > 25) {
				notify("Размер файла не должен превышать 25 МБ.");
				return;
			}

			const validateImage = () => {
				return new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = (event) => {
						const img = new Image();
						img.onload = () => {
							const imgWidth = img.width;
							const imgHeight = img.height;

							if (imgWidth < 800 || imgHeight < 600) {
								reject(new Error("Минимальный размер 800x600 px"));
							} else {
								resolve();
							}
						};
						img.onerror = () => reject(new Error("Failed to load image"));
						img.src = event.target.result;
					};
					reader.onerror = () => reject(new Error("Failed to read file"));
					reader.readAsDataURL(file);
				});
			};

			try {
				await validateImage();

				let formData = new FormData();
				formData.append("id", selectedItem.id);
				formData.append("image", file);
				formData.append("db_name", 'external_menu_products');
				formData.append("column_name", 'image');
				formData.append("path", 'products');

				let response;
				if (!selectedItem.image) {
					response = await FILE('/upload-image', formData, {}, { loader: true });
				} else {
					response = await FILE(`/update-image`, formData, {}, { loader: true });
				}

				if (httpOk(response)) {
					setSelectedItem({ ...selectedItem, 'image': response.data.url });
				}
			} catch (error) {
				notify(error.message);
			}
		}
	}

	async function fileUpload(e) {
		await uploadImageFile(e.target.files[0])
	}

	const { dropProps, dropStyle } = useFileDrop(uploadImageFile)

	function handleSubmit(e) {
		e.preventDefault()
		if (activeTab !== 1) return
		updateProduct(e)
	}

	return (
		<>
			{showRightBar &&
				<>
					<div className="right-sidebar-background"
						onClick={() => setShowRightBar(!showRightBar)}>
					</div>

					<div className="right-sidebar right-sidebar-sticky-footer">
						<div className="right-sidebar-top">
							<div className="position-relative">
								{activeTab === 1 &&
									<div className={`rightbar-sync-icon${showSyncPanel ? ' active' : ''}`}
										onClick={toggleSyncPanel}>
										<div className="btn btn-primary">
											<i className="uil uil-redo"></i>
										</div>
									</div>
								}
								<div className="rightbar-close-icon"
									onClick={() => setShowRightBar(!showRightBar)}>
									<i className="uil uil-times"></i>
								</div>
							</div>
							<h3 className="right-sidebar-title">{t('product')}</h3>
						</div>

						<div className="tabs tabs-sm px-2">
							<ul>
								<li className={activeTab === 1 ? 'active' : ''}
									onClick={() => setActiveTab(1)}>
									<span className="link">{t('main_info')}</span>
								</li>
								<li className={activeTab === 2 ? 'active' : ''}
									onClick={() => setActiveTab(2)}>
									<span className="link">{t('modifiers')}</span>
								</li>
							</ul>
						</div>

						<form onSubmit={handleSubmit} autoComplete="off" className="right-sidebar-form">
							<div className={`right-sidebar-scroll p-2 ${activeTab === 2 ? 'pt-3' : 'pt-0'}`}>
								{activeTab === 1 &&
									<>
										{showSyncPanel &&
											<div className="external-menu-product-sync-panel mb-3">
												<h6 className="mb-2">{t('refresh')}</h6>
												<ExternalMenuSyncForm
													syncOptions={syncOptions}
													setSyncOptions={setSyncOptions}
													idPrefix="product-sync"
												/>
												<div className="d-flex gap-2 w-100 mt-3">
													<button type="button" className="btn btn-outline-warning w-100"
														onClick={closeSyncPanel}>
														{t('cancel')}
													</button>
													<button type="button" className="btn btn-primary w-100"
														onClick={syncProductFromInternal}>
														{t('save')}
													</button>
												</div>
											</div>
										}

										<div className="form-group">
											<label>{t('name2')}<span className="required-mark">*</span></label>
											<input type="text" className="form-control" name="name"
												value={selectedItem.name}
												autoFocus={!showSyncPanel}
												onChange={(e) => setSelectedItem({ ...selectedItem, 'name': e.target.value })} />
										</div>

										<div className="form-group">
											<label>{t("description")}</label>
											<textarea className="form-control" rows="5"
												value={selectedItem.description ?? ''}
												onChange={(e) => setSelectedItem({ ...selectedItem, 'description': e.target.value })}>
											</textarea>
											<span>{t("letters")} - {formatMoney(selectedItem?.description?.length)}|500</span>
										</div>

										<div className="d-flex gap-2">
											<div>
												<div className="form-group w-100">
													<label>{t('sale_price')}<HintIcon text={t('menu_price_from_pos_hint')} /></label>
													<input type="text" className="form-control" name="sale_price"
														placeholder={t('menu_price_from_pos')}
														value={selectedItem.sale_price ?? ''}
														onChange={(e) => setSelectedItem({ ...selectedItem, 'sale_price': e.target.value.replace(/[^0-9.]/g, '') })} />
												</div>
												<div className="form-group w-100">
													<label>{t('weight')}</label>
													<input type="text" className="form-control" name="weight"
														value={selectedItem.weight}
														onChange={(e) => setSelectedItem({ ...selectedItem, 'weight': e.target.value.replace(/[^0-9]/g, '') })} />
												</div>
												<div className="form-group w-100">
													<label>{t('unit')}<span className="required-mark">*</span></label>
													<Select
														isClearable
														options={units}
														value={units.find(option => option.id === selectedItem.unit) || ''}
														onChange={(option) => setSelectedItem({ ...selectedItem, 'unit': option ? option.id : null })}
														placeholder=""
														noOptionsMessage={() => t('list_empty')}
														getOptionLabel={(option) => option.name}
														getOptionValue={(option) => option.id}
													/>
												</div>
											</div>

											<div className="con-upload" style={{ 'width': '300px' }} {...dropProps}>
												<div className="con-img-upload">
													{selectedItem.image &&
														<div className="img-upload" style={dropStyle}>
															<button type="button" className="btn-x-file"
																onClick={() => setSelectedItem({ ...selectedItem, 'image': '' })}>
																<i className="uil-times"></i>
															</button>
															<img src={`https://backend.mison.uz/uploads/products/${selectedItem.image}`}
																style={{ maxWidth: 'none', maxHeight: '100%' }} alt={selectedItem.image} />
														</div>
													}
													{!selectedItem.image &&
														<div className="con-input-upload" style={dropStyle}>
															<input type="file" onChange={(e) => fileUpload(e)} />
															<span className="text-input fz14">
																{t('image2')}
															</span>
														</div>
													}
												</div>
											</div>
										</div>

										<h6 className="mt-3">{t('energy_value_per')}</h6>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('proteins')}</label>
												<input type="number" className="form-control" name="proteins"
													disabled
													value={selectedItem.proteins ?? ''}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'proteins': e.target.value })} />
											</div>
											<div className="form-group w-100">
												<label>{t('fat')}</label>
												<input type="number" className="form-control" name="fat"
													disabled
													value={selectedItem.fat ?? ''}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'fat': e.target.value })} />
											</div>
											<div className="form-group w-100">
												<label>{t('carbohydrates')}</label>
												<input type="number" className="form-control" name="carbohydrates"
													disabled
													value={selectedItem.carbohydrates ?? ''}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'carbohydrates': e.target.value })} />
											</div>
											<div className="form-group w-100">
												<label>{t('calories')}</label>
												<input type="number" className="form-control" name="calories"
													disabled
													value={selectedItem.calories ?? ''}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'calories': e.target.value })} />
											</div>
										</div>

										<div className="d-flex justify-content-between mb-2">
											<p className="fw-600 vertical-center">{t('on_sale')}</p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" name="status"
													checked={selectedItem?.status}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'status': e.target.checked })}
												/>
											</div>
										</div>
									</>
								}

								{activeTab === 2 &&
									<ExternalMenuModifiers externalMenuProductId={selectedItem?.id} />
								}
							</div>

							{activeTab === 1 &&
								<div className="right-sidebar-footer p-2">
									<div className="d-flex gap-2 w-100">
										<button type="button" className="btn btn-outline-warning w-100"
											onClick={() => toggleModalProduct()}>
											{t('cancel')}
										</button>
										<button type="submit" className="btn btn-primary w-100">
											{t('save')}
										</button>
									</div>
								</div>
							}
						</form>
					</div>
				</>
			}
		</>
	)
}

export default Index
